import { Button, Grid, Group, Stack, Title } from '@mantine/core';
import { useState } from 'react';

import { Declaration } from '@e-origin/shared';

import { EucdmCode, MultiLangCodes, NumberInput, TextInput } from '@components';
import { useGenerateWriteOffReport } from '@hooks/batch';
import classes from './FinalizeWriteOff.module.css';
import { UploadWriteOffModal } from './UploadWriteOffModal';

interface FinalizeWriteOffProps {
  declaration: Declaration;
  disabled: boolean;
}

export const FinalizeWriteOff: React.FC<FinalizeWriteOffProps> = ({ declaration, disabled }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);

  const generateWriteOffReportMutation = useGenerateWriteOffReport();

  const packageTypeMatch = declaration.goodsShipmentItems[0].packaging.typeOfPackages === declaration.globalInfo?.previousDocPackageType;
  const numberOfPackagesMatch = declaration.globalInfo?.totalCNTS === declaration.globalInfo?.previousDocTotalNumberOfPackages;
  const quantityMatch = declaration.globalInfo?.totalWeight === declaration.globalInfo?.previousDocTotalQuantity;

  const handelWriteOffReportDownload = () => {
    generateWriteOffReportMutation.mutate(declaration.messageInfo.batch);
  };

  if (!declaration) {
    return null;
  }

  return (
    <>
      <Stack>
        <Title order={4}>Total</Title>
        <Grid mb="sm">
          <Grid.Col span={4}>
            <EucdmCode path="declarationGlobal.totalAmountInvoiced">
              <TextInput
                label="Total amount invoiced"
                value={declaration.globalInfo?.totalAmountInvoiced || ''}
                size="lg"
                disabled
                data-pw="total-amount-invoiced-input"
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <EucdmCode path="goodShipmentGlobal.invoiceCurrency">
              <TextInput label="Invoice currency" value={declaration.globalInfo?.invoiceCurrency || ''} size="lg" disabled data-pw="invoice-currency-input" />
            </EucdmCode>
          </Grid.Col>
        </Grid>

        <Grid mb="sm">
          <Grid.Col span={4}>
            <EucdmCode path="goodsShipmentItems.packaging.typeOfPackages">
              <MultiLangCodes
                value={declaration.goodsShipmentItems[0].packaging.typeOfPackages || ''}
                label="Package Type"
                code="CL017"
                disabled
                size="lg"
                data-pw="package-type-input"
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput label="Total CTNS" value={declaration.globalInfo?.totalCNTS || ''} size="lg" disabled data-pw="total-cnts-input" />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput label="Total Weight" value={declaration.globalInfo?.totalWeight || ''} size="lg" disabled data-pw="total-weight-input" />
          </Grid.Col>
        </Grid>
      </Stack>

      <Stack>
        <Title order={4}>Total Write-off</Title>
        <Grid mb="sm">
          <Grid.Col span={4}>
            <EucdmCode path="goodsShipmentItems.previousDocuments.typeOfPackages">
              <MultiLangCodes
                value={declaration.globalInfo?.previousDocPackageType || ''}
                label="Package Type"
                code="CL017"
                disabled
                data-pw="previous-package-type-input"
                className={packageTypeMatch ? classes.successInput : classes.errorInput}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <EucdmCode path="goodsShipmentItems.previousDocuments.numberOfPackages">
              <NumberInput
                value={declaration.globalInfo?.previousDocTotalNumberOfPackages || 0}
                label="Number of Packages"
                disabled
                data-pw="previous-number-of-packages-input"
                className={numberOfPackagesMatch ? classes.successInput : classes.errorInput}
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <EucdmCode path="goodsShipmentItems.previousDocuments.quantity">
              <NumberInput
                value={declaration.globalInfo?.previousDocTotalQuantity || 0}
                label="Quantity"
                disabled
                data-pw="previous-quantity-input"
                className={quantityMatch ? classes.successInput : classes.errorInput}
              />
            </EucdmCode>
          </Grid.Col>
        </Grid>
      </Stack>

      <Group>
        <Button type="button" onClick={handelWriteOffReportDownload} size="lg" variant="filled">
          Download write-off report
        </Button>
        {!disabled && (
          <Button type="button" onClick={() => setShowUploadModal(true)} size="lg" variant="filled" data-pw="upload-write-off-list-btn">
            Upload write-off list
          </Button>
        )}
      </Group>

      <UploadWriteOffModal opened={showUploadModal} onClose={() => setShowUploadModal(false)} batchId={declaration.messageInfo.batch} />
    </>
  );
};
